import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import type { ProductReferenceCalculationSourceEnum } from "@/features/diagnostics/types";
import type {
    ProductData,
    ProductReference,
    SmartNumberDescription,
    SmartNumberDescriptionDefinition,
    SmartNumberMapping,
} from "@/features/sub-families/types";
import { SmartNumberDescriptor } from "@/types";
import { StringView } from "@/utils/text.ts";

const MAX_REFERENCE_PARTS = 2; // We only care about the first 2 parts

export type ProductReferenceDiagnosticsContext = {
    source: ProductReferenceCalculationSourceEnum;
    products: ProductData;
};

export const getProductReferenceBySmartNumberMapping = (
    smartNumberMapping: SmartNumberMapping,
    smartNumberDescription: SmartNumberDescription,
    diagnostics?: ProductReferenceDiagnosticsContext,
) => {
    const reference = calculateReference(
        smartNumberDescription,
        (definition) => smartNumberMapping[definition.CharacteristicName] ?? "",
    );

    recordProductReferenceCalculation(reference, diagnostics);

    return reference;
};

export const getProductReferenceByUserInput = (
    userInput: string,
    smartNumberDescription: SmartNumberDescription,
    diagnostics?: ProductReferenceDiagnosticsContext,
) => {
    const stringView = new StringView(userInput);

    const reference = calculateReference(smartNumberDescription, (definition) => {
        const amountOfCharacters = +definition.CharacteristicLength;
        if (Number.isNaN(amountOfCharacters)) return "";

        return stringView.destructiveSlice(amountOfCharacters);
    });

    recordProductReferenceCalculation(reference, diagnostics);

    return reference;
};

const calculateReference = (
    smartNumberDescription: SmartNumberDescription,
    getReferencePartByDescription: (definition: SmartNumberDescriptionDefinition) => string,
) => {
    let descriptionIndex = 0;
    let referencePartsIndex = 0;
    let reference = "";

    do {
        const description = smartNumberDescription[descriptionIndex];
        if (description) {
            // Increase the reference parts whenever a separator is detected
            if (description.CharacteristicName === SmartNumberDescriptor.Separator) referencePartsIndex++;

            // Only add the part if we are below the max reference parts. This prevents adding trailing characters
            if (referencePartsIndex < MAX_REFERENCE_PARTS) {
                reference += getReferencePartByDescription(description);
            }
        }

        descriptionIndex++;
    } while (referencePartsIndex < MAX_REFERENCE_PARTS);

    return reference as ProductReference;
};

function recordProductReferenceCalculation(
    reference: ProductReference,
    diagnostics?: ProductReferenceDiagnosticsContext,
) {
    if (!diagnostics) return;

    DiagnosticsStore.collectProductReferenceCalculation({
        source: diagnostics.source,
        reference,
        productFound: reference in diagnostics.products,
    });
}
